//
// Inject the parentSize property into each child
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDisplay } from '../../stores';
import { Button } from '../buttons';
import PaxityProgress from '../PaxityProgress';

import Column from './Column';

const marginPx = 20;
const offsetX = 20;
const offsetY = -20;
const offsetYCorrected = 20;

const FullPopup = function FullPopup(props) {
  const {
    id,
    className,
    contentClassName,
    style,
    children = null,
    onClose,
    dark = true,
    titleBar = true,
    small,
    title,
    subtitle,
    disableBackgroundClick,
    pending,
    customSize,
    position,
    navigationBar,
    titleBarExtra,
    mobileFullScreen,
  } = props;
  const { display } = useDisplay(false);
  const narrow = Boolean(display?.narrow);
  const fullScreen = Boolean(mobileFullScreen && narrow);
  const visualViewportHeight = display?.visualViewportHeight;

  const divRef = useRef();
  const popupRef = useRef();
  const [finalPosition, setFinalPosition] = useState((position)
    ? {
        top: `${marginPx}px`,
        left: `${marginPx}px`,
        init: true,
        // Hide this popup while we compute things
        visibility: 'hidden',
      }
    : null);

  useEffect(
    () => {
      if (divRef) {
        divRef.current.focus();
      }
    },
    [divRef],
  );

  useEffect(
    () => { divRef.current.focus(); },
    [],
  );

  // When the user specifies a position, compute things so the div does
  // not overflow the display
  useEffect(
    () => {
      if (position && popupRef.current && divRef.current && finalPosition?.init) {
        // console.log(`popup sz: w=${popupRef.current.offsetWidth}, h=${popupRef.current.offsetHeight}`);

        const { offsetWidth: width, offsetHeight: height } = popupRef.current;
        // The overlay is the box the popup is positioned in, and measuring it keeps these
        // coordinates in the same px as the popup's own — the shell zoom scales both alike.
        const { clientWidth: overlayWidth, clientHeight: overlayHeight } = divRef.current;

        let finalLeft = position.x + offsetX;
        let finalTop = position.y + offsetY;
        if ((finalLeft + width + marginPx) > overlayWidth) {
          // Need to make popup visible > re-center it
          console.log('trim x');
          finalLeft = overlayWidth - marginPx - width;

          // Try to move the window below
          finalTop = position.y + offsetYCorrected;
        }

        if ((finalTop + height + marginPx) > overlayHeight) {
          // Need to make popup visible > re-center it
          console.log('trim y');
          finalTop = overlayHeight - marginPx - height;
        }

        const newPosition = {
          left: `${finalLeft}px`,
          top: `${finalTop}px`,
          width,
          visibility: 'visible',
        };
        console.log(`newPosition=${JSON.stringify(newPosition)}`);
        setFinalPosition(newPosition);
      }
    },
    [position, finalPosition],
  );

  const handleOnClick = useCallback(
    (event) => {
      if (event.currentTarget.id === 'popupBackground') {
        // console.log('click background disabled');
        if (disableBackgroundClick) return null;
      }

      if (onClose) {
        // console.log('click background enabled');
        return onClose(id);
      }

      return null;
    },
    [onClose, id, disableBackgroundClick],
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // console.log(`key="${event.key}"`);
        if (onClose) onClose(id);
      }
    },
    [onClose, id],
  );

  const outsideClassname = useMemo(
    () => classNames(
      'z-2000 fixed inset-0 flex flex-row items-center justify-center overflow-hidden',
      {
        'backdrop-blur-sm': !fullScreen,
        // A phone has no room for the desktop frame around a dialog
        'p-12': (!fullScreen) && (!narrow),
        'p-4': (!fullScreen) && narrow,
        ' bg-pxBgMuted/70': dark && (!fullScreen),
        ' bg-white/60': (!dark) && (!fullScreen),
      },
    ),
    [dark, fullScreen, narrow],
  );

  const divClassname = useMemo(
    () => classNames(
      'bg-white overflow-hidden',
      {
        'rounded-xl': !fullScreen,
        'w-full h-full pb-[env(safe-area-inset-bottom)]': fullScreen,
        'max-w-full max-h-full': (!customSize) && (!fullScreen),
        'min-w-[50%] min-h-[30%]': (!(small || customSize)) && (!fullScreen),
      },
      className,
    ),
    [className, small, customSize, fullScreen],
  );

  // The overlay is fixed to the layout viewport, which iOS does not shrink when the on-screen
  // keyboard opens; full screen on a phone follows the visual viewport so the popup stays above it.
  const divStyle = useMemo(
    () => ((fullScreen && visualViewportHeight)
      ? { height: `${visualViewportHeight}px` }
      : undefined),
    [fullScreen, visualViewportHeight],
  );

  const popupStyle = useMemo(
    () => {
      let ret = style;
      if (finalPosition) {
        if (!ret) ret = {};

        ret = {
          ...ret,
          ...finalPosition,
        };
      }

      // console.log(`style=${JSON.stringify(ret)}`);
      return ret;
    },
    [style, finalPosition],
  );

  const thisOnKeyPress = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // console.log(`key="${event.key}"`);
        if (onClose) onClose(id);
      }
    },
    [id, onClose],
  );

  const finalContentClassName = useMemo(
    () => classNames(
      'pb-4 pt-0 overflow-y-hidden relative',
      {
        'px-4': !fullScreen,
        'px-2': fullScreen,
      },
      contentClassName,
    ),
    [contentClassName, fullScreen],
  );

  return (
    <div
      className={outsideClassname}
      style={divStyle}
      tabIndex={-1}
      id="popupBackground"
      ref={divRef}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="w-full h-full absolute top-0 left-0"
        onClick={handleOnClick}
        onKeyDown={handleKeyPress}
      />

      <Column
        className={divClassname}
        style={popupStyle}
        onKeyPress={thisOnKeyPress}
        gap={0}
        ref={popupRef}
        absolute={!!position}
        crossStretch
        id="popup"
      >
        {(titleBar)
          ? (
              <div
                className={classNames(
                  'flex flex-row py-2 pl-4 pr-3 relative flex-nowrap overflow-hidden shrink-0',
                  {
                    'h-12 items-center justify-center': !subtitle,
                    'items-start': subtitle,
                  },
                )}
                id="popupTitle"
              >
                {/* <div className="h-8 w-8">
                {/* Icons on the left *}
              </div> */}

                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold truncate">{title || null}</div>
                  {(subtitle)
                    ? <div className="text-sm text-pxTxtMuted truncate">{subtitle}</div>
                    : null}
                </div>

                {(titleBarExtra)
                  ? <div className="mr-2 shrink-0">{titleBarExtra}</div>
                  : null}

                <div className="h-8 w-8">
                  {(onClose)
                    ? (
                        <Button
                          onClick={handleOnClick}
                          imgSrc="/images/svg/gui/crossThin.svg"
                          className="w-full h-full fill-pxPrimary"
                          noFrame
                          imgColorAsCssFill
                        />
                      )
                    : null}
                </div>

              </div>
            )
          : null}

        {navigationBar || null }

        <div
          className={finalContentClassName}
          id="popupContent"
        >
          {children}
        </div>

        { (pending)
          ? (
              <div className="z-2000 w-full h-full absolute top-0 left-0 flex items-center justify-center bg-white/50">
                <PaxityProgress />
              </div>
            )
          : null}
      </Column>
    </div>
  );
};

FullPopup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  style: PropTypes.shape({}),
  onClose: PropTypes.func,
  dark: PropTypes.bool,
  id: PropTypes.string,
  titleBar: PropTypes.bool,
  small: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  disableBackgroundClick: PropTypes.bool,
  pending: PropTypes.bool,
  customSize: PropTypes.bool,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  navigationBar: PropTypes.node,
  titleBarExtra: PropTypes.node,
  // On a phone-sized window, drop the backdrop and fill the screen edge to edge
  mobileFullScreen: PropTypes.bool,
};

export default FullPopup;
